import React, { useEffect, useState } from "react";
import {  Col,  Form,  Input,  Modal,  theme,  Row,  Divider, InputNumber, Select, Spin } from "antd";
import ErrorMessages from "../../common/ErrorMessages";
import { PATCH, POST } from "../../frameworks/HttpClient";
import { URL_PRODUCT, URL_SALE, URL_SETTING, URL_TANK } from "../../constants/urls";
import { PRODUCT_TYPE } from "../../constants/string";
import useDropdownItem from "../../common/useDropdownItem";
import _ from 'lodash';
import { dateToHumanString } from "../../frameworks/Util";

export default function PurchaseCTTListViewModal(props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { open, onClose, target, onUpdate } = props;
  const [errorMessages, setErrorMessages] = useState(null);
  const orderRefValue = Form.useWatch("order_ref", form);

  const [debouncedSupplier, setDebouncedSupplier] = React.useState('');
  const supplierDropdown = useDropdownItem({
    url: URL_SETTING.SELLER_CCT_LATEX, 
    manual: true, label: 'name', value:'id', pagination: true, 
    params: { page_size: 50, name: debouncedSupplier }
  })

  const [debouncedProduct, setDebouncedProduct] = React.useState('');
  const productDropdown = useDropdownItem({
    url: URL_PRODUCT.PRODUCT,
    manual: true, value:'id', pagination: true, 
    label_renderer : (e) => e.short_name ? `${e.name} - ${e.short_name}` : `${e.name}`,
    params: { page_size: 50, name : debouncedProduct, type : PRODUCT_TYPE.CONCENTRATE_LATEX }
  })
  const [debouncedProduceOrder, setDebouncedProduceOrder] = React.useState('');
  const produceOrderDropdown = useDropdownItem({
    url: URL_SALE.PRODUCE_ORDER, 
    manual: true, value:'id', pagination: true, 
    label_renderer: (item) => `[${dateToHumanString(item.created)}] - ${item.lot_no}`,
    params: { page_size: 50, lot_no: debouncedProduceOrder, is_produced: false }, 
  })

  const debounceSupplier = React.useCallback(_.debounce((_searchVal) => { setDebouncedSupplier(_searchVal) }, 800), []);
  const debounceProduct = React.useCallback(_.debounce((_searchVal) => { setDebouncedProduct(_searchVal) }, 800), []);
  const debounceProduceOrder = React.useCallback(_.debounce((_searchVal) => { setDebouncedProduceOrder(_searchVal) }, 800), []);

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (target) {
        form.setFieldsValue(target);
      }
    }
  }, [open]);

  const {
    token: { colorWarning, colorSuccess },
  } = theme.useToken();

  const handleOK = async () => {
    setLoading(true);
    setErrorMessages(null);
    try {
      const data = await form.validateFields();
      if (target?.id) {
        await PATCH(`${URL_TANK.IMPORT_CONCENTRATE_STOCK}${target.id}/`, data)
      } else {
        await POST(URL_TANK.IMPORT_CONCENTRATE_STOCK, data)
      }
      onUpdate();
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if(open)
      supplierDropdown.fetchOptions();
  },[debouncedSupplier])

  React.useEffect(() => {
    if(open)
      productDropdown.fetchOptions();
  },[debouncedProduct])

  React.useEffect(() => {
    if(open)
      produceOrderDropdown.fetchOptions();
  },[debouncedProduceOrder])

  React.useEffect(() => {
    if(open){
      setErrorMessages(null)
      supplierDropdown.fetchOptions();
      productDropdown.fetchOptions();
      produceOrderDropdown.fetchOptions();
    }
  },[open])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      okText={target ? "แก้ไข" : "เพิ่ม"}
      cancelText="ยกเลิก"
      title={target ? "แก้ไขการนำเข้าน้ำยางข้น" : "นำเข้าน้ำยางข้น "}
      width={650}
      onOk={handleOK}
      destroyOnClose
      centered
      okButtonProps={{
        style: { backgroundColor: target ? colorWarning : colorSuccess },
        loading,
      }}
    >
      <ErrorMessages message={errorMessages} />
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              label="บริษัทผู้นำเข้า"
              name="supplier"
              rules={[
                {
                  required: true,
                  message: 'กรุณากรอกข้อมูล'
                },
              ]}
            >
              <Select 
                initialvalues={"-"}
                autoComplete="off" 
                placeholder="บริษัทผู้นำเข้า"
                showSearch
                filterOption={false}
                notFoundContent={supplierDropdown.loading ? <Spin size="small" /> : "-"}
                onSearch={debounceSupplier}
                options={supplierDropdown.options}
                allowClear={"-"}/> 
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item 
              label="ล็อตนำเข้า" 
              name="source_lot_no"                
              rules={[
                  {
                    required: true,
                    message: 'กรุณากรอกข้อมูล'
                  },
                ]}>
              <Input placeholder={'ล็อตนำเข้า'}/>
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item 
              label="สินค้า" 
              name="product" 
              placeholder={'สินค้า'}
              rules={[
                {
                  required: true,
                  message: 'กรุณากรอกข้อมูล'
                },
              ]}
            >
                <Select 
                  initialvalues={"-"}
                  autoComplete="off" 
                  placeholder="สินค้า"
                  showSearch
                  filterOption={false}
                  disabled={orderRefValue ? true : false}
                  notFoundContent={productDropdown.loading ? <Spin size="small" /> : "-"}
                  onSearch={debounceProduct}
                  options={productDropdown.options}
                  allowClear={"-"}/> 
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="น้ำหนัก (Kg)"
              name="weight"
              rules={[
                {
                  required: true,
                  message: 'กรุณากรอกข้อมูล'
                },
              ]}
            >
              <InputNumber style={{ width : '100%' }} placeholder={'น้ำหนัก (Kg)'}/>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="%DRC" name="drc">
              <Input placeholder={'%DRC'}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="ราคา/กก." name="unit_price">
              <InputNumber style={{ width : '100%' }} placeholder={'ราคา/กก.'}/>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="ราคารวม" name="net_price">
              <InputNumber style={{ width : '100%' }} placeholder={'ราคารวม'}/>
            </Form.Item>
          </Col>
          <Divider>ข้อมูลปลายทาง</Divider>
          <Col span={12}>
            <Form.Item 
              label="Lot No (ล็อตปลายทาง)" 
              name="lot_no" 
              rules={[
                {
                  required: true,
                  message: 'กรุณากรอกข้อมูล'
                },
              ]}
            >
              <Input placeholder={'Lot No (ล็อตปลายทาง)'} disabled={orderRefValue ? true : false}/>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="ใบแจ้งผลิต" name="order_ref" >
              <Select 
                initialvalues={"-"}
                autoComplete="off" 
                placeholder="ใบแจ้งผลิต"
                showSearch
                filterOption={false}
                notFoundContent={produceOrderDropdown.loading ? <Spin size="small" /> : "-"}
                onSearch={debounceProduceOrder}
                options={_.get(target, 'order_ref', null) ? 
                  [{ value: _.get(target, 'order_ref', null), label: `ล็อตที่: ${_.get(target, 'lot_no', '')} ชนิดสินค้า: ${_.get(target, 'product_name', '')}` }] : 
                  produceOrderDropdown.options
                }
                allowClear={"-"}
                disabled={_.get(target, 'order_ref', null) ? true : false}
                onSelect={(_, option) => {
                  form.setFieldValue('lot_no', option.lot_no)
                  form.setFieldValue('product', option.product)
                }}
                onClear={() => {
                  form.setFieldValue('lot_no', null)
                  form.setFieldValue('product', null)
                }}/>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
